import {
  EditorContent,
  useEditor,
  type UseEditorOptions,
  type JSONContent,
} from '@tiptap/react';
import { useSelector } from 'react-redux';
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import type { Editor as EditorCore } from '@tiptap/core';
import { EditorApi, EditorProps } from './types';
import { EditorContainerSizeContext, EditorContext } from './Context';
import { useExtensions } from './useExtensions';
import LinkBubbleMenu from '../menus/LinkBubbleMenu/LinkBubbleMenu';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import DaDeviationBubbleMenu from '../../extensions/extension-deviation/components/DaDeviationBubbleMenu';
import DaVideoBubbleMenu from '../../extensions/extension-video/components/DaVideoBubbleMenu';
import { getContentFromStorage, isWriterContent } from '../utils/tiptap';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { Selection } from '@tiptap/pm/state';
import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import DaGalleryBubbleMenu from '../../extensions/extension-gallery/components/DaGalleryBubbleMenu';
import { setRefs } from '@wix/da-ds/pkg/utils/refHelpers';
import { autoScroll } from './helpers/autoScroll';
import DaGifBubbleMenu from '../../extensions/extension-gif/components/DaGifBubbleMenu';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '../../extensions/extension-muro/components/MicroMuroComment';
import DaDeviationThumbBubbleMenu from '../../extensions/extension-deviation-thumb/components/DaDeviationThumbBubbleMenu';
import { useTranslation } from 'react-i18next';
import ddt from '@wix/da-ddt';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { JsonSchemaCleaner } from '../utils/json';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';

import baseStyles from './styles/base.scss';
import baseViewerStyles from '../Viewer/styles/base.scss';

export interface EditorRef {
  editor: EditorCore | null;
}

const noop = () => {};

const Editor: FC<EditorProps> = ({
  className,
  children,
  onReady,
  onChange,
  onFocus,
  onBlur,
  onDestroy,
  onSelectMedia,
  onManageMedia,
  autoFocus,
  placeholder,
  placeholderOptions,
  characterCountOptions,
  initialContent,
  initialSelection,
  scrollContainerRef,
  selfScrollContainer,
  extensionConfig,
  externalRenderers,
  cookieType,
  defaultSize,
  presetId,
  autoScrollWhenWithin,
  autoScrollDistance,
  withinModal,
  currentUser,
  containerPadding,
  presetRuntimeOptions,
  preserveWhitespace,
}) => {
  const { t } = useTranslation('editor');
  const apiRef = useRef<EditorApi | null>(null);
  const editorRef = useRef<EditorCore | null>(null);
  // We'll expose the editor ref via context
  const { setRef, setIsReady, setIsEmpty } = useContext(EditorContext);
  const { setContainerSize } = useContext(EditorContainerSizeContext);

  // Crash recovery
  const [editorCrashed, setEditorCrashed] = useState(false);
  const handleRecoverData = useCallback(() => {
    setEditorCrashed(false);
    try {
      const fixContent = getContentFromStorage(initialContent);
      if (fixContent) {
        const cleaner = new JsonSchemaCleaner();
        const cleaned = cleaner.clean(fixContent);
        editorRef.current?.commands.setContent(cleaned, true);
        ddt.alert('editor', 'Editor recovery attempted', ['editor', 'fatal'], {
          content: JSON.stringify(cleaned),
          before: JSON.stringify(fixContent),
          presetId,
        });
      }
      editorRef.current?.setEditable(true, false);
    } catch (e) {
      editorRef.current?.setEditable(true, false);
      ddt.alertOnError(
        e,
        'editor',
        'Editor recovery failed',
        ['editor', 'fatal'],
        {
          content: JSON.stringify(initialContent),
          presetId,
        }
      );
      editorRef.current?.commands.setContent(
        {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [],
            },
          ],
        },
        true
      );
    }
  }, [initialContent, presetId]);

  const csrfToken = useSelector(state => getConfigKey(state, 'csrfToken'));
  const shouldFakeApiRequests = useSelector(state =>
    getConfigKey(state, 'fakeApiRequests')
  );
  const { MicroComponent, MiniComponent, MobileComponent } =
    useContext(MuroDepContext);

  const selfScrollRef = useRef<HTMLDivElement>(null);
  const { ref, dimensions } = useMeasureElementCookie(cookieType, {
    cookieDim: 'width',
    defaultSize,
  });

  const isMobile = useContext(MobileContext);
  const handleChange: UseEditorOptions['onUpdate'] = useCallback(
    ({ editor, transaction }) => {
      onChange && onChange(editor, transaction);
      autoScrollWhenWithin &&
        autoScroll(
          autoScrollWhenWithin,
          autoScrollDistance ?? 50,
          editor,
          scrollContainerRef?.current
        );
      setIsEmpty(editor.isEmpty);
    },
    [
      autoScrollDistance,
      autoScrollWhenWithin,
      onChange,
      scrollContainerRef,
      setIsEmpty,
    ]
  );

  const exts = useExtensions(extensionConfig, {
    placeholder,
    placeholderOptions,
    characterCountOptions,
    presetRuntimeOptions,
  });

  const onFocusHandler = useCallback(
    props => {
      onFocus && onFocus(props);

      // Mobile
      // This is a fix for mobile where the keyboard is animated and it takes
      // time to appear, tiptap already fires scrollIntoView on nearly all inserts
      // but because of the animation the screen changes size after the tiptap call.
      // This then adds a delay so the the scroll happens at the right time.
      setTimeout(() => {
        if (isMobile) {
          props.editor.commands.scrollIntoView();
        }
      }, 600);
    },
    [isMobile, onFocus]
  );

  let content: JSONContent | undefined;
  try {
    content = getContentFromStorage(initialContent);

    if (content && content.type === 'doc') {
      // Deal with invalid nodes in the document, document only allows block type nodes
      // no inlines like text/hardBreak
      // If this is needed more often we should make a processor
      content.content = content.content?.filter(
        n => n.type !== 'hardBreak' && n.type !== 'text'
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to load the editor markup', e);
  }

  // This handle content errors where the editor can't continue
  const onContentErrorHandler = useCallback(
    ({ editor, error }) => {
      const emitUpdate = false;
      editor.setEditable(false, emitUpdate);
      setEditorCrashed(true);
      ddt.alertOnError(
        error,
        'editor',
        'Bad content for tiptap',
        ['editor', 'fatal'],
        {
          content: JSON.stringify(content),
          presetId,
        }
      );
    },
    [content, presetId]
  );

  const isWriter = isWriterContent(initialContent);
  const editorOpts: UseEditorOptions = useMemo(() => {
    const parseOptions = preserveWhitespace
      ? { preserveWhitespace: 'full' }
      : undefined;
    return omitBy(
      {
        onUpdate: handleChange,
        extensions: exts,
        autofocus: autoFocus,
        onFocus: onFocusHandler,
        onBlur,
        onDestroy,
        content,
        enableContentCheck: isWriter ? false : true,
        onContentError: onContentErrorHandler,
        enableInputRules: isMobile ? ['da-damz'] : ['da-emote', 'da-damz'],
        parseOptions,
      },
      isUndefined
    );
  }, [
    autoFocus,
    content,
    exts,
    handleChange,
    isMobile,
    isWriter,
    onBlur,
    onContentErrorHandler,
    onDestroy,
    onFocusHandler,
    preserveWhitespace,
  ]);

  const editor = useEditor(editorOpts, []);
  editorRef.current = editor;

  useEffect(() => {
    // Set initial selection
    if (initialSelection && editor?.state) {
      const selection = Selection.fromJSON(editor.state.doc, initialSelection);
      editor.commands.focus();
      editor.commands.setTextSelection(selection);
    }
  }, [editor, initialSelection]);

  useEffect(() => {
    if (setRef) {
      setRef(editor);
    }
    if (editor) {
      externalRenderers.muroMicro = MicroComponent;
      externalRenderers.muroMini = MiniComponent;
      externalRenderers.muroMobile = MobileComponent;

      editor.storage['da-currentUser'] = currentUser;
      editor.storage['da-ext-config'] = extensionConfig;
      editor.storage['da-ext-renderers'] = externalRenderers;
      editor.storage['da-callbacks'] = {
        onSelectMedia,
        onManageMedia,
      };
      editor.storage['da-presetId'] = presetId;
      editor.storage['da-inside-modal'] = withinModal;

      // These are only used when an extension isn't able to use redux-saga based networking.
      editor.storage['da-csrf-token'] = csrfToken ?? 'csrf-token-missing';
      editor.storage['da-fake-api-requests'] = shouldFakeApiRequests;
    }

    // Signal readyness
    if (!apiRef.current && editor) {
      apiRef.current = {
        focusEditor() {
          editorRef.current?.commands.focus();
        },
        blurEditor() {
          editorRef.current?.commands.blur();
        },
        clearEditor() {
          editorRef.current?.commands.clearContent(true);
        },
        getStateForSave() {
          return {
            version: '1',
            document: editorRef.current?.getJSON() ?? {},
          };
        },
        isEmpty() {
          return !!editorRef.current?.isEmpty;
        },
        getCurrentSelection() {
          return editorRef.current?.view.state.selection as any;
        },
        setSelection(selection) {
          editorRef.current?.commands.setTextSelection(selection.$from.pos);
        },
        uploadFile(file) {
          editorRef.current?.commands.daUploadFile?.(file);
        },
        hasMedia() {
          let nodeCount = 0;
          editorRef.current?.state.doc.descendants(node => {
            if (!['text', 'paragraph', 'da-emote'].includes(node.type.name)) {
              nodeCount++;
            }
          });
          return nodeCount > 0;
        },
        charCount() {
          if (!editorRef.current?.storage?.characterCount) {
            ddt.warn('editor', 'You need to enable the chat count extension');
            return 0;
          }
          return editorRef.current?.storage?.characterCount.characters();
        },
        setContent(con, emit, parseOpt, opt) {
          return editorRef.current?.commands.setContent(
            con,
            emit,
            parseOpt,
            opt
          );
        },
      };
      setIsReady(true);
      onReady && onReady(apiRef.current);
    }
  }, [
    editor,
    extensionConfig,
    externalRenderers,
    onSelectMedia,
    onReady,
    setRef,
    setIsReady,
    presetId,
    shouldFakeApiRequests,
    csrfToken,
    onManageMedia,
    withinModal,
    currentUser,
    MicroComponent,
    MiniComponent,
    MobileComponent,
  ]);

  useEffect(() => {
    if (containerPadding) {
      setContainerSize && setContainerSize(dimensions.width - containerPadding);
    } else {
      setContainerSize && setContainerSize(dimensions.width);
    }
  }, [containerPadding, dimensions.width, setContainerSize]);

  const setScrollAndMeasureRefs = useCallback(
    node => {
      // This fixes the scroll inside the form from just randomly breaking
      // I tried all sorts, removing all scroll listeners etc and this is only
      // thing that fixes it.
      if (selfScrollContainer) {
        const stopProp = e => e.stopPropagation();
        node?.addEventListener('scroll', stopProp, { passive: true });
        node?.addEventListener('wheel', stopProp, { passive: true });
      }
      setRefs([ref, selfScrollRef], node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selfScrollContainer]
  );

  const scrollContainer = selfScrollContainer
    ? selfScrollRef
    : scrollContainerRef;

  if (!editor) {
    // TODO is this an error state?
    return null;
  }

  return (
    <div
      ref={setScrollAndMeasureRefs}
      className={cn(
        baseStyles['base-tiptap'],
        baseViewerStyles['base-tiptap'],
        className,
        'editor-scrollable'
      )}
    >
      <PopupContext.Provider
        value={{ isInPopup: true, setScrollableContentRef: noop }}
      >
        <EditorContent editor={editor} className="editor-wrapper" />
        {extensionConfig.link && (
          <LinkBubbleMenu scrollContainerRef={scrollContainer} />
        )}
        {extensionConfig.deviation && (
          <DaDeviationBubbleMenu scrollContainerRef={scrollContainer} />
        )}
        {extensionConfig.daVideo && (
          <DaVideoBubbleMenu scrollContainerRef={scrollContainerRef} />
        )}
        {extensionConfig.deviationThumb && (
          <DaDeviationThumbBubbleMenu scrollContainerRef={scrollContainer} />
        )}
        {extensionConfig.gallery && (
          <DaGalleryBubbleMenu scrollContainerRef={scrollContainer} />
        )}
        {extensionConfig.gif && (
          <DaGifBubbleMenu scrollContainerRef={scrollContainer} />
        )}
        {children?.(editor, scrollContainer)}
        {extensionConfig.muro && <MicroMuro editor={editor} />}

        <MessageModal
          isOpen={editorCrashed}
          footerButtons={[
            <SolidButton
              key="recover"
              size="large"
              variant="destructive-red"
              onClick={handleRecoverData}
            >
              {t('editor.error.invalid.content.button')}
            </SolidButton>,
          ]}
          title={t('editor.error.invalid.content.title')}
          message={t('editor.error.invalid.content.message')}
          variant="horizontal"
        ></MessageModal>
      </PopupContext.Provider>
    </div>
  );
};
Editor.displayName = 'Editor';

export default Editor;
